import type { ApolloError } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import type { DocumentNode } from 'graphql';

export type PortfolioAccessState = 'checking' | 'allowed' | 'denied';

// portfolio-service throws bare `new Error(...)` for every authz verdict, which apollo-server 3
// stamps INTERNAL_SERVER_ERROR — the same code a DB failure gets — so extensions.code cannot
// discriminate. Match on the substrings the backend itself classifies these throws by.
const ACCESS_DENIED_PATTERNS = [
  /is not part of this team/i, // authorizationRules.ts:228, authorization.ts:16
  /could not find portfolio with id/i, // portfolio DataLoader, via @argAuthz — deleted or nonexistent
  /is not under team/i, // legacy helpers.ts:14 wording, matched defensively
];

/**
 * True only for a definitive "you may not see this portfolio" verdict. A transport failure is
 * never a verdict: offline, 5xx and timeouts must leave the user where they are.
 */
export function isPortfolioAccessDeniedError(error?: ApolloError | null): boolean {
  if (!error) return false;
  if (error.networkError) return false;
  return (error.graphQLErrors ?? []).some((gqlError) => {
    const message = [gqlError?.message, (gqlError?.extensions as { message?: unknown })?.message]
      .filter((part): part is string => typeof part === 'string')
      .join(' ');
    return ACCESS_DENIED_PATTERNS.some((pattern) => pattern.test(message));
  });
}

/**
 * Whether errorLink should stay silent. READS only: RequirePortfolioAccess recovers those by
 * redirecting. A denied MUTATION has no such recovery, and most mutation hooks declare no local
 * onError — silencing the global toast there makes a rejected write look like it succeeded.
 */
export function isSilencedAccessDenial(query: DocumentNode, error?: ApolloError | null): boolean {
  const definition = getMainDefinition(query);
  const isQuery = definition.kind === 'OperationDefinition' && definition.operation === 'query';
  return isQuery && isPortfolioAccessDeniedError(error);
}

type PortfolioAccessArgs = {
  /** parseInt of the :portfolioID route param — may be NaN. */
  portfolioId: number;
  /** The meta query's own variables.teamId; undefined when not yet known. */
  teamId?: number | null;
  loading: boolean;
  error?: ApolloError | null;
  /** data?.portfolioV2 — nullable in the schema. */
  portfolio?: { portfolioId?: number | null; teamId?: number | null } | null;
};

/**
 * Decides whether /portfolios/:portfolioID may render. Three states, not a boolean: "still
 * checking" must stay distinguishable from "genuinely absent", or a cache-and-network refetch
 * would bounce a legitimate user mid-flight.
 */
export function resolvePortfolioAccess({
  portfolioId,
  teamId,
  loading,
  error,
  portfolio,
}: PortfolioAccessArgs): PortfolioAccessState {
  // /portfolios/abc and /portfolios/0. No query can settle these: the hook's `skip: !portfolioId`
  // means NaN never reaches the network, so nothing would ever arrive to judge. Decide from the URL.
  if (!Number.isInteger(portfolioId) || portfolioId <= 0) return 'denied';

  // Never judge before the team resolves: with teamId 0 the backend answers "User is not part of
  // this team" for a portfolio the user CAN see.
  if (teamId != null && teamId <= 0) return 'checking';

  if (isPortfolioAccessDeniedError(error)) return 'denied';
  // Any other failure is not a verdict — keep rendering and let the route surface it.
  if (error) return 'checking';

  // portfolioV2 is nullable, so a SETTLED query with no portfolio is a denial; an in-flight one
  // is not.
  if (portfolio == null) return loading ? 'checking' : 'denied';

  // Never judge identity against a payload that may still describe the PREVIOUS portfolio: on a
  // switch from 42 to 99 the route param changes before the new result lands, so an in-flight
  // render can pair the new id with the old payload. Denying there would bounce the user out of
  // an ordinary portfolio switch.
  if (loading) return 'checking';

  // Identity mismatch on a settled payload. Stay lenient when the fields are absent so a partial
  // cache read never bounces a legitimate user.
  if (portfolio.portfolioId != null && portfolio.portfolioId !== portfolioId) return 'denied';
  if (teamId != null && portfolio.teamId != null && portfolio.teamId !== teamId) return 'denied';

  return 'allowed';
}
